import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import { PageHeader } from '@/components/PageHeader';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEOHead 
        title="Terms of Service - GrowthLens"
        description="Review GrowthLens's terms of service for using our B2B marketing assessment platform, including user responsibilities and service guidelines."
        canonical="https://growthlens.in/terms-of-service"
      />
      <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        heading="Terms of Service"
        description="Last updated: September 28, 2025"
      />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        
        <Card className="card-enhanced hover-lift animate-fade-in transition-all duration-300 border-primary/10">
          <CardContent className="space-y-8 text-sm leading-relaxed px-6 md:px-8 py-8">
            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">1. Acceptance of Terms</h2>
              <p>By accessing and using this assessment platform, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to these terms, you should not use this service.</p>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">2. Description of Service</h2>
              <p>Our platform provides online assessment tools and services designed to evaluate various skills, competencies, and characteristics. We offer both free and premium features, including detailed reports and analytics.</p>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">3. User Accounts</h2>
              <div className="space-y-3">
                <p>To access certain features, you must create an account and provide accurate, complete information. You are responsible for:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Maintaining the confidentiality of your account credentials</li>
                  <li>All activities that occur under your account</li>
                  <li>Notifying us immediately of any unauthorized use</li>
                  <li>Ensuring your account information remains current and accurate</li>
                </ul>
              </div>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">4. Acceptable Use</h2>
              <p>You agree to use the service only for lawful purposes and in accordance with these terms. You must not:</p>
              <ul className="list-disc list-inside space-y-2 mt-3">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe on intellectual property rights</li>
                <li>Transmit harmful, offensive, or inappropriate content</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with or disrupt the service</li>
                <li>Share assessment content or attempt to compromise test integrity</li>
                <li>Use automated systems to access the service without permission</li>
              </ul>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">5. Assessment Integrity</h2>
              <p>When taking assessments, you agree to:</p>
              <ul className="list-disc list-inside space-y-2 mt-3">
                <li>Complete assessments honestly and independently</li>
                <li>Not share, copy, or reproduce assessment questions</li>
                <li>Not seek external assistance unless explicitly permitted</li>
                <li>Respect time limits and assessment guidelines</li>
              </ul>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">6. Payment and Billing</h2>
              <div className="space-y-3">
                <p>For premium features and services:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Payment is due in advance for subscription services</li>
                  <li>All fees are non-refundable except as required by law</li>
                  <li>We reserve the right to change pricing with appropriate notice</li>
                  <li>Failure to pay may result in service suspension or termination</li>
                </ul>
              </div>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">7. Intellectual Property</h2>
              <p>All content, features, and functionality of the service are owned by us or our licensors and are protected by copyright, trademark, and other intellectual property laws. You may not modify, reproduce, or distribute our content without permission.</p>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">8. Privacy and Data Use</h2>
              <p>Your privacy is important to us. Our use of your personal information is governed by our <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>, which is incorporated into these terms by reference.</p>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">9. Assessment Results and Reports</h2>
              <div className="space-y-3">
                <p>Regarding your assessment results:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Results are provided for informational and developmental purposes</li>
                  <li>We do not guarantee the accuracy or completeness of results</li>
                  <li>Results should not be used as the sole basis for important decisions</li>
                  <li>You retain access to your results as long as your account remains active</li>
                </ul>
              </div>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">10. Service Availability</h2>
              <p>We strive to maintain service availability but do not guarantee uninterrupted access. We may temporarily suspend the service for maintenance, updates, or other operational reasons with or without notice.</p>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">11. Disclaimers and Limitations</h2>
              <div className="space-y-3">
                <p>THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li>MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE</li>
                  <li>NON-INFRINGEMENT AND ACCURACY</li>
                  <li>UNINTERRUPTED OR ERROR-FREE OPERATION</li>
                </ul>
                <p className="mt-3">OUR LIABILITY TO YOU IS LIMITED TO THE AMOUNT YOU HAVE PAID FOR THE SERVICE IN THE TWELVE MONTHS PRECEDING THE CLAIM.</p>
              </div>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">12. Indemnification</h2>
              <p>You agree to indemnify and hold us harmless from any claims, damages, or expenses arising from your use of the service, violation of these terms, or infringement of any rights of third parties.</p>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">13. Termination</h2>
              <p>We may terminate or suspend your account and access to the service at any time, with or without cause or notice, for conduct that violates these terms or is otherwise harmful to our business or other users.</p>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">14. Governing Law</h2>
              <p>These terms are governed by and construed in accordance with applicable laws. Any disputes will be resolved through binding arbitration or in courts of competent jurisdiction.</p>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">15. Changes to Terms</h2>
              <p>We reserve the right to modify these terms at any time. We will notify users of material changes by posting the updated terms on this page. Continued use of the service constitutes acceptance of the modified terms.</p>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">16. Contact Information</h2>
              <p>If you have questions about these terms, please contact us through our <Link to="/contact" className="text-primary hover:underline">contact page</Link>.</p>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">17. Severability</h2>
              <p>If any provision of these terms is found to be unenforceable, the remaining provisions will continue to be valid and enforceable.</p>
            </section>
          </CardContent>
        </Card>
      </div>
      </div>
    </>
  );
};

export default TermsOfService;